/**
 * Rendu des accessoires decoratifs.
 *
 * Les geometries sont procedurales: aucune texture, tout est sommets et
 * couleurs. Une touffe d'herbe est faite de vrais brins courbes, larges au pied
 * et effiles a la pointe, avec un degrade du pied (fonce) vers la pointe
 * (claire); une fleur a des petales, une tige et un coeur. La couleur finale
 * est le produit de `instanceColor` (teinte tiree par le generateur) et d'une
 * couleur par sommet cuite dans la geometrie.
 *
 * Les instances sont regroupees par (type, case de terrain), comme les noeuds
 * de ressources: un `InstancedMesh` se dessine en entier ou pas du tout,
 * donc un maillage unique par type enverrait les dizaines de milliers
 * d'accessoires du monde a chaque image. Un maillage par (type, case) porte une
 * sphere englobante serree, et three.js elimine tout seul les lots hors champ.
 *
 * L'ondulation ne reconstruit rien: chaque image, quelques lots voient leurs
 * matrices recomposees et seuls ces morceaux repartent a la carte.
 */
import * as THREE from 'three'
import { CHUNK_SIZE } from '../../shared/constants.ts'
import type { Batiment } from '../../shared/types/monde.ts'
import type { Prop, PropKind } from '../../shared/types/world.ts'
import { MARGE_COUVERTURE_DECOR, couvreLePoint, rayonCouverture } from '../../shared/sim/batiments.ts'
import { journal } from '../log/journal.ts'

/** Types dont les instances ondulent. */
const ONDULENT: readonly PropKind[] = ['herbe', 'fleur']

/**
 * Nombre d'instances reanimees par image, tous lots ondulants confondus.
 *
 * IL SE MESURE SUR CE QU'ON VOIT, PAS SUR CE QUI EXISTE. Le balayage passait
 * sur TOUS les lots ondulants du monde, y compris ceux qui sont derriere la
 * camera et ceux que `reglerDetail` a deja rendus invisibles: le jour ou l'ile
 * 2 a triple le nombre de touffes du monde (18 000 -> 54 000), un tour complet
 * du vent serait passe d'une quinzaine d'images a une cinquantaine, et l'herbe
 * aurait ondule par vagues lentes, visibles a l'oeil.
 *
 * Relever le budget aurait coute ce temps de calcul a chaque image, pour animer
 * des touffes que personne ne regarde. On saute donc les lots hors champ, et le
 * balayage reste aussi court sur une ile de sept cents metres que sur une de
 * deux cents. Meme raisonnement que « s'ecarter coute MOINS cher »: le decor au
 * loin s'efface de lui-meme.
 */
const BUDGET_ANIM = 1200

/**
 * Cote de la maille de regroupement, par type.
 *
 * Herbe et fleurs pesent 96 % des triangles du decor: elles meritent la maille
 * fine du chunk, qui elimine agressivement. Les cailloux, coquillages,
 * champignons et glacons sont rares et legers; a la maille du chunk ils
 * couteraient deux cents lots, donc autant d'appels de dessin potentiels, pour
 * quelques milliers de triangles. On les regroupe donc par deux chunks.
 */
function mailleDe(kind: PropKind): number {
  return kind === 'herbe' || kind === 'fleur' ? CHUNK_SIZE : CHUNK_SIZE * 2
}

interface Partie {
  geo: THREE.BufferGeometry
  /** Multiplicateur de couleur applique a toute la partie. */
  teinte: [number, number, number]
  /** Multiplicateur supplementaire du bas vers le haut de la partie. */
  degrade?: [number, number]
}

/** Concatene des parties non indexees en une geometrie unique avec couleurs. */
function fusionner(parties: Partie[]): THREE.BufferGeometry {
  let total = 0
  const brutes: THREE.BufferGeometry[] = []
  for (const p of parties) {
    const g = p.geo.index ? p.geo.toNonIndexed() : p.geo
    if (g !== p.geo) p.geo.dispose()
    brutes.push(g)
    total += (g.getAttribute('position') as THREE.BufferAttribute).count
  }

  const positions = new Float32Array(total * 3)
  const normales = new Float32Array(total * 3)
  const couleurs = new Float32Array(total * 3)
  let o = 0
  for (let i = 0; i < brutes.length; i++) {
    const g = brutes[i]
    const p = parties[i]
    if (!g || !p) continue
    const pa = g.getAttribute('position') as THREE.BufferAttribute
    const na = g.getAttribute('normal') as THREE.BufferAttribute | undefined
    const ca = g.getAttribute('color') as THREE.BufferAttribute | undefined
    positions.set(pa.array as Float32Array, o * 3)
    if (na) normales.set(na.array as Float32Array, o * 3)

    let yMin = Infinity
    let yMax = -Infinity
    for (let v = 0; v < pa.count; v++) {
      const y = pa.getY(v)
      if (y < yMin) yMin = y
      if (y > yMax) yMax = y
    }
    const etendue = yMax - yMin || 1
    for (let v = 0; v < pa.count; v++) {
      const t = p.degrade ? (pa.getY(v) - yMin) / etendue : 0
      const f = p.degrade ? p.degrade[0] + (p.degrade[1] - p.degrade[0]) * t : 1
      const k = (o + v) * 3
      // Une partie peut deja porter ses propres couleurs (brins d'herbe,
      // petales): on les multiplie alors par la teinte de la partie.
      couleurs[k] = p.teinte[0] * f * (ca ? ca.getX(v) : 1)
      couleurs[k + 1] = p.teinte[1] * f * (ca ? ca.getY(v) : 1)
      couleurs[k + 2] = p.teinte[2] * f * (ca ? ca.getZ(v) : 1)
    }
    o += pa.count
    g.dispose()
  }

  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  geo.setAttribute('normal', new THREE.BufferAttribute(normales, 3))
  geo.setAttribute('color', new THREE.BufferAttribute(couleurs, 3))
  return geo
}

/** Deforme les sommets d'un solide pour qu'il ne ressemble pas a un solide. */
function bosseler(geo: THREE.BufferGeometry, force: number, graine: number): THREE.BufferGeometry {
  const pa = geo.getAttribute('position') as THREE.BufferAttribute
  let h = graine >>> 0
  for (let i = 0; i < pa.count; i++) {
    h = (Math.imul(h ^ (h >>> 15), 2246822519) + 374761393) >>> 0
    const r = (h >>> 8) / 16777216 - 0.5
    pa.setXYZ(i, pa.getX(i) * (1 + r * force), pa.getY(i) * (1 - r * force), pa.getZ(i) * (1 + r * force * 0.7))
  }
  geo.computeVertexNormals()
  return geo
}

// ------------------------------------------------------------------ herbe

/** Suite deterministe simple: les geometries doivent etre identiques partout. */
function suite(graine: number): () => number {
  let h = graine >>> 0
  return () => {
    h = (Math.imul(h ^ (h >>> 15), 2246822519) + 374761393) >>> 0
    return (h >>> 8) / 16777216
  }
}

/**
 * Etapes le long d'un brin. Deux quads, quatre triangles: vu de dessus a 45
 * degres, le nombre de brins compte plus que la finesse de la courbe, donc on
 * depense le budget en brins (six) plutot qu'en segments.
 */
const SEGMENTS_BRIN = 2

/**
 * Un brin d'herbe: une laniere courbe, large au pied et effilee a la pointe.
 *
 * Le brin s'inscrit dans un plan vertical oriente par `azimut` et se couche
 * progressivement selon `inclinaison`. Les normales sont ramenees vers la
 * verticale: un ruban strictement vertical prend mal la lumiere d'un soleil
 * haut et vire au noir, alors qu'une touffe doit rester vive.
 */
function brinHerbe(
  azimut: number,
  inclinaison: number,
  hauteur: number,
  largeur: number,
  torsion: number,
): THREE.BufferGeometry {
  const dx = Math.cos(azimut)
  const dz = Math.sin(azimut)
  // Perpendiculaire horizontale: c'est la direction de la largeur du brin.
  const px = -dz
  const pz = dx

  const pos: number[] = []
  const nor: number[] = []
  const col: number[] = []

  // Points de l'axe du brin, par integration de l'angle d'inclinaison.
  const ax: number[] = []
  const ay: number[] = []
  const az: number[] = []
  const dl = hauteur / SEGMENTS_BRIN
  let cx = 0
  let cy = 0
  let cz = 0
  ax.push(0)
  ay.push(0)
  az.push(0)
  for (let s = 1; s <= SEGMENTS_BRIN; s++) {
    const t = (s - 0.5) / SEGMENTS_BRIN
    const angle = inclinaison * t * t
    cx += Math.sin(angle) * dx * dl
    cz += Math.sin(angle) * dz * dl
    cy += Math.cos(angle) * dl
    ax.push(cx)
    ay.push(cy)
    az.push(cz)
  }

  const demi = (s: number): number => largeur * 0.5 * (1 - s) * (1 - s * 0.45) + 0.004
  const teinte = (s: number, sortie: [number, number, number]): void => {
    // Pied fonce et un peu bleute, pointe claire et doree: c'est ce degrade qui
    // empeche la touffe de se lire comme une echarde palote.
    sortie[0] = 0.34 + 0.6 * s * s
    sortie[1] = 0.44 + 0.5 * s
    sortie[2] = 0.32 + 0.2 * s * s
  }

  const c0: [number, number, number] = [0, 0, 0]
  const c1: [number, number, number] = [0, 0, 0]
  for (let s = 0; s < SEGMENTS_BRIN; s++) {
    const t0 = s / SEGMENTS_BRIN
    const t1 = (s + 1) / SEGMENTS_BRIN
    const w0 = demi(t0)
    const w1 = demi(t1)
    // Legere torsion: les deux bords ne restent pas dans le meme plan.
    const g0 = torsion * t0
    const g1 = torsion * t1
    const x0 = ax[s] ?? 0
    const y0 = ay[s] ?? 0
    const z0 = az[s] ?? 0
    const x1 = ax[s + 1] ?? 0
    const y1 = ay[s + 1] ?? 0
    const z1 = az[s + 1] ?? 0
    teinte(t0, c0)
    teinte(t1, c1)

    const a = [x0 - px * w0, y0 - g0 * w0, z0 - pz * w0]
    const b = [x0 + px * w0, y0 + g0 * w0, z0 + pz * w0]
    const c = [x1 + px * w1, y1 + g1 * w1, z1 + pz * w1]
    const d = [x1 - px * w1, y1 - g1 * w1, z1 - pz * w1]
    const quad = [a, b, c, a, c, d]
    const couls = [c0, c0, c1, c0, c1, c1]
    for (let v = 0; v < 6; v++) {
      const p = quad[v] as number[]
      const q = couls[v] as [number, number, number]
      pos.push(p[0] ?? 0, p[1] ?? 0, p[2] ?? 0)
      // Normale mi-verticale, mi-face du brin.
      nor.push(px * 0.35, 0.9, pz * 0.35)
      col.push(q[0], q[1], q[2])
    }
  }

  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3))
  geo.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(nor), 3))
  geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(col), 3))
  return geo
}

/** Touffe de cinq brins: 30 triangles, inclinaisons et hauteurs variees. */
function geometrieHerbe(): THREE.BufferGeometry {
  const a = suite(0x51f00d)
  const parties: Partie[] = []
  const brins = 6
  for (let i = 0; i < brins; i++) {
    // Azimuts repartis mais desordonnes: une touffe reguliere fait rosace.
    const azimut = (i / brins) * Math.PI * 2 + a() * 0.9
    const hauteur = 0.42 + a() * 0.28
    const inclinaison = 0.5 + a() * 0.8
    const largeur = 0.1 + a() * 0.055
    const torsion = a() * 0.5 - 0.25
    parties.push({
      geo: brinHerbe(azimut, inclinaison, hauteur, largeur, torsion),
      teinte: [1, 1, 1],
    })
  }
  return fusionner(parties)
}

// ------------------------------------------------------------------ fleur

/** Petale: un simple quad incline, plus large au milieu qu'a l'attache. */
function petale(azimut: number, inclinaison: number, longueur: number, largeur: number): THREE.BufferGeometry {
  const dx = Math.cos(azimut)
  const dz = Math.sin(azimut)
  const px = -dz
  const pz = dx
  const monte = Math.sin(inclinaison)
  const avance = Math.cos(inclinaison)

  const bx = dx * longueur * 0.28 * avance
  const bz = dz * longueur * 0.28 * avance
  const by = monte * longueur * 0.28
  const tx = dx * longueur * avance
  const tz = dz * longueur * avance
  const ty = monte * longueur

  const w = largeur * 0.5
  const pos = [
    0, 0, 0,
    bx - px * w, by, bz - pz * w,
    bx + px * w, by, bz + pz * w,
    bx - px * w, by, bz - pz * w,
    tx, ty, tz,
    bx + px * w, by, bz + pz * w,
  ]
  const nor: number[] = []
  const col: number[] = []
  // Attache sombre, bout lumineux: le petale ne se lit pas comme un confetti.
  const teintes = [0.86, 0.98, 0.98, 0.98, 1.24, 0.98]
  for (let v = 0; v < 6; v++) {
    nor.push(0, 1, 0)
    const f = teintes[v] ?? 1
    col.push(f, f, f)
  }
  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pos), 3))
  geo.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(nor), 3))
  geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(col), 3))
  return geo
}

function geometrieFleur(): THREE.BufferGeometry {
  const parties: Partie[] = []
  // Tige: un ruban croise, deux quads, legerement penche.
  const tige = new THREE.PlaneGeometry(0.022, 0.26)
  tige.translate(0, 0.13, 0)
  tige.rotateZ(0.08)
  const tigeB = tige.clone()
  tigeB.rotateY(Math.PI / 2)
  parties.push({ geo: tige, teinte: [0.36, 0.66, 0.3], degrade: [0.75, 1.15] })
  parties.push({ geo: tigeB, teinte: [0.36, 0.66, 0.3], degrade: [0.75, 1.15] })

  // Deux feuilles basses, pour asseoir la fleur dans l'herbe.
  for (let i = 0; i < 2; i++) {
    const f = petale(i * 2.4 + 0.7, 0.35, 0.095, 0.055)
    f.translate(0, 0.06, 0)
    parties.push({ geo: f, teinte: [0.34, 0.62, 0.28] })
  }

  // Cinq petales en corolle.
  const a = suite(0xf10e05)
  for (let i = 0; i < 5; i++) {
    const p = petale((i / 5) * Math.PI * 2 + a() * 0.25, 0.5 + a() * 0.25, 0.135, 0.092)
    p.translate(0, 0.28, 0)
    parties.push({ geo: p, teinte: [1.12, 1.12, 1.12] })
  }

  // Coeur: un petit cone dore.
  const coeur = new THREE.ConeGeometry(0.034, 0.05, 5, 1)
  coeur.translate(0, 0.305, 0)
  parties.push({ geo: coeur, teinte: [1.45, 1.3, 0.55] })

  return fusionner(parties)
}

// ------------------------------------------------------------------ autres

function geometrieCoquillage(): THREE.BufferGeometry {
  const cone = new THREE.ConeGeometry(0.11, 0.075, 7, 1)
  cone.scale(1, 1, 0.6)
  cone.translate(0, 0.037, 0)
  return fusionner([{ geo: cone, teinte: [1, 1, 1], degrade: [0.82, 1.2] }])
}

function geometrieChampignon(): THREE.BufferGeometry {
  const tige = new THREE.CylinderGeometry(0.032, 0.042, 0.13, 6, 1)
  tige.translate(0, 0.065, 0)
  const chapeau = new THREE.ConeGeometry(0.105, 0.095, 8, 1)
  chapeau.translate(0, 0.175, 0)
  return fusionner([
    { geo: tige, teinte: [1.45, 1.4, 1.3] },
    { geo: chapeau, teinte: [1, 1, 1], degrade: [0.85, 1.18] },
  ])
}

function geometrieCaillou(): THREE.BufferGeometry {
  const roc = bosseler(new THREE.DodecahedronGeometry(0.15, 0), 0.45, 0x9e3779b9)
  roc.scale(1, 0.66, 1)
  roc.translate(0, 0.07, 0)
  return fusionner([{ geo: roc, teinte: [1, 1, 1], degrade: [0.78, 1.15] }])
}

function geometrieGlacon(): THREE.BufferGeometry {
  const bloc = bosseler(new THREE.IcosahedronGeometry(0.2, 0), 0.5, 0x1b873f)
  bloc.scale(0.85, 1.6, 0.85)
  bloc.translate(0, 0.28, 0)
  return fusionner([{ geo: bloc, teinte: [1, 1, 1], degrade: [0.7, 1.3] }])
}

function geometriePour(kind: PropKind): THREE.BufferGeometry {
  switch (kind) {
    case 'herbe':
      return geometrieHerbe()
    case 'fleur':
      return geometrieFleur()
    case 'coquillage':
      return geometrieCoquillage()
    case 'champignon':
      return geometrieChampignon()
    case 'caillou':
      return geometrieCaillou()
    case 'glacon':
      return geometrieGlacon()
    default: {
      // Les gros decors (arbres, rochers, cactus) sont poses ailleurs; on garde
      // une forme de secours pour ne jamais perdre une instance.
      const c = new THREE.ConeGeometry(0.35, 1.1, 6, 1)
      c.translate(0, 0.55, 0)
      return fusionner([{ geo: c, teinte: [1, 1, 1], degrade: [0.7, 1.2] }])
    }
  }
}

// ------------------------------------------------------------------ classe

interface Lot {
  mesh: THREE.InstancedMesh
  /** x, y, z, rot, scale par instance. */
  base: Float32Array
  kind: PropKind
  /** Vrai quand l'aplatissement a change et doit etre reecrit. */
  aplatiSale?: boolean
  /**
   * Date de repousse par instance, en temps de jeu (0 = intacte).
   *
   * Quand la voiture passe, l'herbe et les fleurs se couchent au lieu d'etre
   * detruites: rien ne disparait, tout revient. Allouee paresseusement, parce
   * que la plupart des lots ne seront jamais ecrases.
   */
  aplati: Float32Array | null
  /**
   * Instances escamotees parce qu'une construction les recouvre (1 = cachee).
   *
   * Le semis du decor ne connait que la graine du monde: il est pose bien avant
   * qu'on sache ce qui est bati, donc l'herbe et les fleurs poussaient dans le
   * salon. On ne les retire pas de l'InstancedMesh — un tampon d'instances ne se
   * reconstruit pas a chaque pose — on les met a l'echelle zero, comme un noeud
   * de ressource epuise. Allouee paresseusement: la quasi-totalite des lots ne
   * verra jamais une seule construction.
   */
  couvert: Uint8Array | null
}

/**
 * Taille apparente minimale d'un accessoire pour qu'il vaille la peine d'etre
 * dessine, en pixels de hauteur d'ecran.
 *
 * Un brin d'herbe fait environ 30 cm. Quand on dezoome au maximum, la camera
 * embrasse 90 m de haut: la touffe pese alors 3 pixels, mais l'herbe represente
 * a elle seule 414 000 des triangles de la scene. On l'escamote donc au-dela
 * d'un certain zoom: invisible a l'oeil, et c'est la moitie de la charge
 * geometrique qui disparait exactement quand on en a le plus besoin.
 */
const PIXELS_UTILES = 8

/** Hauteur approximative de chaque type d'accessoire, en metres. */
const HAUTEUR_PROP: Partial<Record<PropKind, number>> = {
  herbe: 0.32,
  fleur: 0.42,
  champignon: 0.22,
  coquillage: 0.14,
  caillou: 0.3,
  glacon: 0.7,
}

const mScratch = new THREE.Matrix4()
const qScratch = new THREE.Quaternion()
const eScratch = new THREE.Euler()
const posScratch = new THREE.Vector3()
const echScratch = new THREE.Vector3()

export class RenduProps {
  readonly groupe = new THREE.Group()

  private scene: THREE.Scene
  private lots: Lot[] = []
  private geometries: THREE.BufferGeometry[] = []
  private matPlan: THREE.MeshLambertMaterial
  private matSolide: THREE.MeshLambertMaterial
  private animes: Lot[] = []
  private curseur = 0
  private total = 0

  constructor(scene: THREE.Scene, props: Prop[]) {
    this.scene = scene
    this.groupe.name = 'props'
    this.matPlan = new THREE.MeshLambertMaterial({ vertexColors: true, side: THREE.DoubleSide })
    this.matSolide = new THREE.MeshLambertMaterial({ vertexColors: true })

    // Un paquet par (type, case): c'est la maille d'elimination hors champ.
    const paquets = new Map<string, { kind: PropKind; liste: Prop[] }>()
    for (const p of props) {
      const maille = mailleDe(p.kind)
      const cx = Math.floor(p.x / maille)
      const cz = Math.floor(p.z / maille)
      const cle = `${p.kind}:${cx}:${cz}`
      const q = paquets.get(cle)
      if (q) q.liste.push(p)
      else paquets.set(cle, { kind: p.kind, liste: [p] })
    }

    // Les geometries sont partagees par tous les lots d'un meme type.
    const geoParType = new Map<PropKind, THREE.BufferGeometry>()
    const couleur = new THREE.Color()

    for (const [cle, paquet] of paquets) {
      const kind = paquet.kind
      let geo = geoParType.get(kind)
      if (!geo) {
        geo = geometriePour(kind)
        geo.computeBoundingSphere()
        geoParType.set(kind, geo)
        this.geometries.push(geo)
      }
      const liste = paquet.liste
      const plan = kind === 'herbe' || kind === 'fleur'
      const ondule = ONDULENT.includes(kind)
      const mesh = new THREE.InstancedMesh(geo, plan ? this.matPlan : this.matSolide, liste.length)
      mesh.name = `props_${cle}`
      mesh.castShadow = false
      mesh.receiveShadow = true
      mesh.instanceMatrix.setUsage(ondule ? THREE.DynamicDrawUsage : THREE.StaticDrawUsage)

      const base = new Float32Array(liste.length * 5)
      let minX = Infinity
      let maxX = -Infinity
      let minZ = Infinity
      let maxZ = -Infinity
      let minY = Infinity
      let maxY = -Infinity
      let echelleMax = 1
      for (let i = 0; i < liste.length; i++) {
        const p = liste[i]
        if (!p) continue
        base[i * 5] = p.x
        base[i * 5 + 1] = p.y
        base[i * 5 + 2] = p.z
        base[i * 5 + 3] = p.rot
        base[i * 5 + 4] = p.scale
        eScratch.set(0, p.rot, 0)
        qScratch.setFromEuler(eScratch)
        posScratch.set(p.x, p.y, p.z)
        echScratch.setScalar(p.scale)
        mScratch.compose(posScratch, qScratch, echScratch)
        mesh.setMatrixAt(i, mScratch)
        mesh.setColorAt(i, couleur.setHex(p.teinte))
        if (p.x < minX) minX = p.x
        if (p.x > maxX) maxX = p.x
        if (p.z < minZ) minZ = p.z
        if (p.z > maxZ) maxZ = p.z
        if (p.y < minY) minY = p.y
        if (p.y > maxY) maxY = p.y
        if (p.scale > echelleMax) echelleMax = p.scale
      }
      mesh.instanceMatrix.needsUpdate = true
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true

      // Sphere englobante serree, calculee a la main: `computeBoundingSphere`
      // d'un InstancedMesh ignore les matrices d'instance.
      const rayonModele = (geo.boundingSphere?.radius ?? 0.5) * echelleMax
      const centre = new THREE.Vector3((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2)
      const demi = Math.hypot(maxX - minX, maxY - minY, maxZ - minZ) / 2
      mesh.boundingSphere = new THREE.Sphere(centre, demi + rayonModele + 0.2)
      mesh.computeBoundingSphere = (): void => {}
      mesh.frustumCulled = true

      const lot: Lot = { mesh, base, kind: paquet.kind, aplati: null, couvert: null }
      this.groupe.add(mesh)
      this.lots.push(lot)
      this.total += liste.length
      if (ondule) this.animes.push(lot)
    }

    scene.add(this.groupe)
    journal.info('props', `${this.total} instances · ${this.lots.length} lots (type x case)`)
  }

  /**
   * Anime le vent. Les lots ondulants sont parcourus en ronde: chaque image on
   * en reanime assez pour tenir le budget, et le tour complet dure quelques
   * images, ce qui suffit largement a l'oeil pour une herbe qui balance.
   */
  /**
   * Ajuste le niveau de detail selon le zoom.
   *
   * `demiHauteurMonde` est la demi-hauteur du frustum orthographique en metres
   * (le `zoom` du rig). `hauteurEcran` est la hauteur du tampon de dessin.
   */
  reglerDetail(demiHauteurMonde: number, hauteurEcran: number): void {
    const pixelsParMetre = hauteurEcran / Math.max(1e-6, demiHauteurMonde * 2)
    let change = false
    for (const lot of this.lots) {
      const h = HAUTEUR_PROP[lot.kind]
      // Les types sans hauteur connue restent toujours visibles.
      const visible = h === undefined ? true : h * pixelsParMetre >= PIXELS_UTILES
      if (lot.mesh.visible !== visible) {
        lot.mesh.visible = visible
        change = true
      }
    }
    if (change) this.detailCourant = pixelsParMetre
  }

  /** Derniere echelle appliquee, pour le journal et les tests. */
  private detailCourant = 0

  get echelleDetail(): number {
    return this.detailCourant
  }

  maj(tempsSec: number, centre?: { x: number; z: number }, portee = Infinity): void {
    const n = this.animes.length
    if (n === 0) return
    let budget = BUDGET_ANIM
    let visites = 0
    while (budget > 0 && visites < n) {
      const lot = this.animes[this.curseur]
      this.curseur = (this.curseur + 1) % n
      visites++
      if (!lot) continue
      const compte = lot.mesh.count
      if (compte === 0) continue
      // Ce que le niveau de detail a deja efface n'a pas besoin de vent.
      if (!lot.mesh.visible) continue
      if (centre && portee !== Infinity) {
        const sphere = lot.mesh.geometry.boundingSphere
        const rayon = sphere ? sphere.radius : 0
        const dx = (sphere ? sphere.center.x : 0) - centre.x
        const dz = (sphere ? sphere.center.z : 0) - centre.z
        const limite = portee + rayon
        if (dx * dx + dz * dz > limite * limite) continue
      }
      for (let i = 0; i < compte; i++) {
        const j = i * 5
        const x = lot.base[j] ?? 0
        const y = lot.base[j + 1] ?? 0
        const z = lot.base[j + 2] ?? 0
        const rot = lot.base[j + 3] ?? 0
        const ech = lot.base[j + 4] ?? 1
        const couche = lot.aplati ? (lot.aplati[i] ?? 0) > 0 : false
        const phase = tempsSec * 1.6 + x * 0.55 + z * 0.37
        if (lot.couvert && (lot.couvert[i] ?? 0) !== 0) {
          // Sous une construction: echelle zero. On la repose a chaque tour du
          // vent, sinon l'ondulation la redresserait dans le salon.
          eScratch.set(0, rot, 0)
          qScratch.setFromEuler(eScratch)
          posScratch.set(x, y, z)
          echScratch.setScalar(0)
        } else if (couche) {
          // Couchee: presque a plat, inclinee dans le sens du passage, et elle
          // ne bouge plus au vent. Elle est ecrasee, pas supprimee.
          eScratch.set(1.15, rot, 0.12, 'YXZ')
          qScratch.setFromEuler(eScratch)
          posScratch.set(x, y, z)
          echScratch.set(ech * 1.1, ech * 0.3, ech * 1.1)
        } else {
          eScratch.set(Math.sin(phase) * 0.13, rot, Math.cos(phase * 0.8) * 0.11, 'YXZ')
          qScratch.setFromEuler(eScratch)
          posScratch.set(x, y, z)
          echScratch.setScalar(ech)
        }
        mScratch.compose(posScratch, qScratch, echScratch)
        lot.mesh.setMatrixAt(i, mScratch)
      }
      lot.mesh.instanceMatrix.needsUpdate = true
      budget -= compte
    }
  }

  /**
   * Repose une instance dans son etat au repos: sa transformation d'origine, ou
   * l'echelle zero si une construction la recouvre.
   */
  private reposerInstance(lot: Lot, i: number): void {
    const j = i * 5
    const cache = lot.couvert ? (lot.couvert[i] ?? 0) !== 0 : false
    eScratch.set(0, lot.base[j + 3] ?? 0, 0)
    qScratch.setFromEuler(eScratch)
    posScratch.set(lot.base[j] ?? 0, lot.base[j + 1] ?? 0, lot.base[j + 2] ?? 0)
    echScratch.setScalar(cache ? 0 : (lot.base[j + 4] ?? 1))
    mScratch.compose(posScratch, qScratch, echScratch)
    lot.mesh.setMatrixAt(i, mScratch)
  }

  /**
   * Escamote le decor recouvert par une construction, et lui seul.
   *
   * RECALCULE TOUT a partir de la liste passee, au lieu de tenir un compteur par
   * instance: deux emprises voisines peuvent couvrir la meme touffe (la marge
   * exigee entre deux batiments est plus petite qu'une touffe), et un simple
   * drapeau pose a la construction se serait leve a la demolition du voisin,
   * laissant l'herbe repousser dans une maison toujours debout. Repartir de la
   * verite est aussi ce qui rend la methode idempotente: on l'appelle au
   * chargement, a la pose, a la demolition, et a l'arrivee d'un batiment pose
   * par un autre joueur, toujours de la meme facon.
   *
   * Cout: un test de disque par (lot, batiment) — quelques milliers de
   * comparaisons — puis `couvreLePoint` sur les seules instances des lots
   * reellement traverses. C'est O(batiments) PAR EVENEMENT, jamais par image.
   */
  majCouverture(batiments: readonly Batiment[]): number {
    let caches = 0
    // Un rayon par batiment, calcule UNE fois: le recalculer dans la boucle des
    // lots le referait deux cent cinquante fois pour rien.
    // `rayonCouverture` et non `rayonEmprise`: une aerogare traine vingt-cinq
    // metres de piste, et un disque taille sur la seule emprise la laisserait
    // dehors — l'herbe pousserait au travers du bitume.
    const rayons = batiments.map((b) => rayonCouverture(b, MARGE_COUVERTURE_DECOR))

    for (const lot of this.lots) {
      const compte = lot.base.length / 5
      // Repartir de zero: `precedent` sert a ne reecrire que ce qui change.
      const precedent = lot.couvert
      let courant: Uint8Array | null = null
      const s = lot.mesh.boundingSphere

      for (let k = 0; k < batiments.length; k++) {
        const b = batiments[k]
        if (!b) continue
        // Elimination par region: la sphere englobante du lot contre le disque
        // qui contient l'emprise. Sans elle, poser une cloture ferait balayer
        // les dizaines de milliers d'accessoires du monde.
        if (s) {
          const dx = s.center.x - b.pos.x
          const dz = s.center.z - b.pos.z
          const porte = (rayons[k] ?? 0) + s.radius
          if (dx * dx + dz * dz > porte * porte) continue
        }
        for (let i = 0; i < compte; i++) {
          const j = i * 5
          if (!couvreLePoint(b, lot.base[j] ?? 0, lot.base[j + 2] ?? 0, MARGE_COUVERTURE_DECOR)) continue
          if (!courant) courant = new Uint8Array(compte)
          courant[i] = 1
        }
      }

      if (courant) for (const v of courant) if (v !== 0) caches++
      // Rien n'a change dans ce lot: on ne touche pas a ses matrices.
      if (!precedent && !courant) continue
      lot.couvert = courant
      let change = false
      for (let i = 0; i < compte; i++) {
        const avant = precedent ? (precedent[i] ?? 0) : 0
        const apres = courant ? (courant[i] ?? 0) : 0
        if (avant === apres) continue
        this.reposerInstance(lot, i)
        change = true
      }
      if (change) lot.mesh.instanceMatrix.needsUpdate = true
    }
    return caches
  }

  /** Nombre d'accessoires actuellement escamotes par une construction. */
  nombreCouverts(): number {
    let n = 0
    for (const lot of this.lots) {
      if (!lot.couvert) continue
      for (const v of lot.couvert) if (v !== 0) n++
    }
    return n
  }

  /**
   * Couche la vegetation sur le passage d'un vehicule.
   *
   * `rayon` en metres, `repousseA` en temps de jeu. Seuls l'herbe et les fleurs
   * se couchent: un rocher ou un glacon ne s'aplatit pas, et de toute facon la
   * voiture ne peut pas y entrer.
   */
  coucher(x: number, z: number, rayon: number, repousseA: number): number {
    let touchees = 0
    const r2 = rayon * rayon
    for (const lot of this.animes) {
      if (!lot) continue
      // La sphere englobante du lot evite de parcourir tout le monde.
      const b = lot.mesh.boundingSphere
      if (b) {
        const dx = b.center.x - x
        const dz = b.center.z - z
        const porte = b.radius + rayon
        if (dx * dx + dz * dz > porte * porte) continue
      }
      const compte = lot.base.length / 5
      for (let i = 0; i < compte; i++) {
        const j = i * 5
        const px = (lot.base[j] ?? 0) - x
        const pz = (lot.base[j + 2] ?? 0) - z
        if (px * px + pz * pz > r2) continue
        if (!lot.aplati) lot.aplati = new Float32Array(compte)
        // On ne raccourcit jamais un delai deja en cours: le dernier passage
        // decide, donc rouler deux fois au meme endroit prolonge la trace.
        if (repousseA > (lot.aplati[i] ?? 0)) lot.aplati[i] = repousseA
        touchees++
      }
      lot.aplatiSale = true
    }
    return touchees
  }

  /**
   * Redresse ce qui a fini de repousser. A appeler avec le temps de jeu; c'est
   * bon marche parce qu'on ne touche que les lots reellement couches.
   */
  majRepousse(tempsJeu: number): number {
    let redressees = 0
    for (const lot of this.animes) {
      if (!lot?.aplati) continue
      let reste = false
      for (let i = 0; i < lot.aplati.length; i++) {
        const t = lot.aplati[i] ?? 0
        if (t === 0) continue
        if (tempsJeu >= t) {
          lot.aplati[i] = 0
          redressees++
        } else {
          reste = true
        }
      }
      if (!reste) lot.aplati = null
      lot.aplatiSale = true
    }
    return redressees
  }

  /** Nombre d'accessoires actuellement couches. */
  nombreCouches(): number {
    let n = 0
    for (const lot of this.animes) {
      if (!lot?.aplati) continue
      for (const t of lot.aplati) if (t > 0) n++
    }
    return n
  }

  nombreInstances(): number {
    return this.total
  }

  /** Nombre de lots, donc d'appels de dessin potentiels avant elimination. */
  nombreLots(): number {
    return this.lots.length
  }

  dispose(): void {
    this.scene.remove(this.groupe)
    for (const lot of this.lots) lot.mesh.dispose()
    for (const g of this.geometries) g.dispose()
    this.lots.length = 0
    this.geometries.length = 0
    this.animes.length = 0
    this.groupe.clear()
    this.matPlan.dispose()
    this.matSolide.dispose()
  }
}
